import { useState } from "react";
import {
  App,
  // Button,
  ConfigProvider,
  Flex,
  Input,
  Space,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
// import PermissionsIcon from "@/assets/permissions.svg?react";
import EditIcon from "@/assets/edit.svg?react";
import { ApiError } from "@/api/client";
import { saveEventRequestUrl } from "@/api/events";
import BrandButton from "@/components/BrandButton";
import { useCurrentApp } from "@/hooks/useCurrentApp";
import EventConfirmIcon from "@/assets/event_confirm.svg?react";
import { Hint } from "./Hint";
import { IconBtn } from "@/components/IconBtn";
import { SectionCard } from "./SectionCard";
import type { EventPushConfig } from "./types";

function isHttpUrl(value: string) {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

export function SubscriptionMethod({
  config,
  onSave,
  // onGoEncryption,
}: {
  config: EventPushConfig;
  onSave: (next: Pick<EventPushConfig, "requestUrl" | "deliveryMode">) => void;
  onGoEncryption: () => void;
}) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const app = useCurrentApp();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftUrl, setDraftUrl] = useState(config.requestUrl);

  const url = draftUrl.trim();
  const formatInvalid = url.length > 0 && (url.length > 512 || !isHttpUrl(url));
  const canSave = url.length > 0 && url.length <= 512 && isHttpUrl(url);

  const openEdit = () => {
    setDraftUrl(config.requestUrl);
    setEditing(true);
  };

  const cancel = () => {
    setDraftUrl(config.requestUrl);
    setEditing(false);
  };

  const save = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      await saveEventRequestUrl(app.id, url);
      onSave({ requestUrl: url, deliveryMode: "webhook" });
      setEditing(false);
      message.success(t("events.saved"));
    } catch (err) {
      message.error(
        err instanceof ApiError && err.message
          ? err.message
          : t("events.saveFailed"),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard>
      <Typography.Title level={5} className="!m-0">
        {t("events.subscriptionMethod")}
      </Typography.Title>

      {editing ? (
        <Space orientation="vertical" size={16} className="w-full">
          <Space size={6}>
            <Typography.Text type="secondary">
              {t("events.webhookUrl")}
            </Typography.Text>
            <Hint title={t("events.webhookHint")} />
          </Space>

          <Flex align="center" gap={8}>
            <div className="w-5.5 h-5.5 rounded-full bg-accent flex justify-center items-center">
              <EventConfirmIcon />
            </div>
            <span>{t("events.deliveryWebhook")}</span>
          </Flex>
          <div className="flex w-full flex-col gap-1">
            <div className="flex w-full items-center gap-4 rounded-lg border border-line p-3">
              <ConfigProvider
                theme={{
                  components: {
                    Input: {
                      activeShadow: "none",
                      hoverBorderColor: "transparent",
                      activeBorderColor: "transparent",
                    },
                  },
                }}
              >
                <Input
                  value={draftUrl}
                  maxLength={512}
                  status={formatInvalid ? "error" : undefined}
                  placeholder={t("events.urlPlaceholder")}
                  onChange={(e) => setDraftUrl(e.target.value)}
                  variant="borderless"
                  className="flex-1"
                />
              </ConfigProvider>
              <span className="shrink-0 text-xs text-muted">
                {draftUrl.length}/512
              </span>
              <div className="flex gap-4">
                <BrandButton variant="outline" onClick={cancel}>
                  {t("common.cancel")}
                </BrandButton>
                <BrandButton
                  disabled={!canSave}
                  loading={saving}
                  onClick={() => void save()}
                >
                  {t("common.save")}
                </BrandButton>
              </div>
            </div>
            {formatInvalid ? (
              <Typography.Text type="danger">
                {t("events.urlInvalid")}
              </Typography.Text>
            ) : null}
          </div>

          {/* <Button
            icon={<PermissionsIcon />}
            onClick={onGoEncryption}
            className="!bg-accent/10 !text-accent !border-0"
          >
            {t("events.goEncryption")}
          </Button> */}
        </Space>
      ) : (
        <Space>
          <Typography.Text type="secondary">
            {config.requestUrl || t("events.urlNotConfigured")}
          </Typography.Text>
          <IconBtn label={t("common.edit")} onClick={openEdit}>
            <EditIcon />
          </IconBtn>
        </Space>
      )}
    </SectionCard>
  );
}
